import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Divider,
  CardContent,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import RoomIcon from "@mui/icons-material/Room";

import { StyledCard } from "../common/StyledCard";
import { TimetableSlotResponseDto } from "../../shared/dtos/timetableSlot/TimetableSlotResponseDto";

const formatTime = (timeString?: string | null): string => {
  if (!timeString) return "";
  const [hour, minute] = timeString.split(":");
  const date = new Date();
  date.setHours(parseInt(hour, 10), parseInt(minute, 10));
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const capitalize = (str?: string | null): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

interface TimetableSlotCardProps {
  slot: TimetableSlotResponseDto;
  onEdit?: () => void;
}

const TimetableSlotCard: React.FC<TimetableSlotCardProps> = ({
  slot,
  onEdit,
}) => {
  return (
    <StyledCard>
      <CardContent
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {slot.subjectName}
            </Typography>
            <Chip
              label={slot.subjectCode}
              size="small"
              color="secondary"
              variant="outlined"
              sx={{
                mt: 0.5,
                height: 20,
                fontSize: "0.7rem",
                fontWeight: 700,
              }}
            />
          </Box>
          <Stack direction="row">
            {onEdit && (
              <Tooltip title="Edit Timetable Slot">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Body Details */}
        <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <EventIcon fontSize="small" sx={{ color: "action.active" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <b>Day:</b> {capitalize(slot.dayOfWeek)}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <AccessTimeIcon fontSize="small" sx={{ color: "action.active" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <b>Time:</b> {formatTime(slot.startTime)} -{" "}
              {formatTime(slot.endTime)}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <PersonIcon fontSize="small" sx={{ color: "action.active" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <b>Teacher:</b> {slot.teacherName || "Unassigned"}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <RoomIcon fontSize="small" sx={{ color: "action.active" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <b>Location:</b> {slot.location || "TBA"}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

export default TimetableSlotCard;
